/**
 * <OverflowAlarm /> — dev-only regression watchdog.
 *
 * Renders a flashing red banner whenever the current page's scrollWidth
 * exceeds the viewport — i.e. anytime horizontal overflow appears.
 *
 * Why:
 *   Iter25/26 audit found the navbar minimum width was 1220px before we
 *   shipped the icon-only collapse. The bug only became visible when the
 *   USER reported zoom problems — not when we developed. This banner
 *   surfaces overflow regressions IMMEDIATELY for any developer running
 *   locally, before they ever ship.
 *
 * Behavior:
 *   - Active only when NODE_ENV !== 'production' (zero footprint in prod).
 *   - Polls `document.documentElement.scrollWidth` every 750ms.
 *   - Also reacts to window resize.
 *   - Banner shows the offending overflow amount + the route name + a
 *     hint to inspect the largest overflowing element.
 *   - Click "Dismiss" to hide for the current session (sessionStorage).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const IS_DEV = process.env.NODE_ENV !== 'production';
const SESSION_KEY = 'cartie:overflow-alarm-dismissed';

const findWorstOffender = () => {
  const winW = window.innerWidth;
  let worst = { tag: '', selector: '', right: 0, width: 0 };
  // Restrict to elements that are likely the culprit (not popovers / toasts).
  const candidates = document.querySelectorAll('main, section, header, nav, footer, div[class], table, pre, code');
  candidates.forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.right > winW && rect.width > worst.width) {
      const cls = (el.className || '').toString().split(/\s+/).slice(0, 3).join('.');
      worst = {
        tag: el.tagName.toLowerCase(),
        selector: cls ? `${el.tagName.toLowerCase()}.${cls}` : el.tagName.toLowerCase(),
        right: Math.round(rect.right),
        width: Math.round(rect.width),
      };
    }
  });
  return worst;
};

const OverflowAlarm = () => {
  const location = useLocation();
  const [overflow, setOverflow] = useState(0);
  const [offender, setOffender] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch { return false; }
  });

  const check = useCallback(() => {
    if (!IS_DEV) return;
    const docW = document.documentElement.scrollWidth;
    const winW = window.innerWidth;
    const diff = docW - winW;
    if (diff > 0) {
      setOverflow(diff);
      setOffender(findWorstOffender());
    } else if (overflow !== 0) {
      setOverflow(0);
      setOffender(null);
    }
  }, [overflow]);

  useEffect(() => {
    if (!IS_DEV) return;
    check();
    const tid = setInterval(check, 750);
    window.addEventListener('resize', check);
    return () => {
      clearInterval(tid);
      window.removeEventListener('resize', check);
    };
  }, [check]);

  // Re-check whenever the route changes so we re-evaluate on navigation.
  useEffect(() => { if (IS_DEV) setTimeout(check, 350); }, [location.pathname, check]);

  if (!IS_DEV || dismissed || overflow <= 0) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="dev-overflow-alarm"
      className="fixed top-2 right-2 z-[9999] max-w-sm rounded-lg border-2 border-red-500 bg-red-950/95 text-red-50 shadow-2xl shadow-red-500/40 px-4 py-3 animate-pulse"
      style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 11 }}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <div className="font-bold text-red-200 mb-0.5">⚠ HORIZONTAL OVERFLOW · DEV ONLY</div>
          <div className="text-red-100 leading-snug">
            <span className="text-red-300">{location.pathname}</span> overflows viewport by{' '}
            <span className="text-amber-200 font-bold">+{overflow}px</span>
          </div>
          {offender && (
            <div className="mt-1.5 text-red-200 leading-snug">
              Top culprit: <span className="text-amber-200">{offender.selector}</span>
              {' '}(w={offender.width}, right={offender.right})
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-red-300 hover:text-white text-xs font-bold ml-2"
          title="Dismiss for this session"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default OverflowAlarm;
